import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box, Checkbox } from "@material-ui/core";
import { useFormik } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useParams } from "react-router-dom";
import { Autocomplete, Input } from "components/Shared/mui-formik-inputs";
import { SupplierGroupSchema } from "constants/validation-schemas";
import { PATHS } from "util/appConstants";
import FormCancelSaveButton from "components/Shared/FormCancelSaveButtons";

const useStyles = makeStyles({
  _heading: {
    font: "normal normal normal 28px/40px Questrial",
    color: "#121212",
  },
  _icons: {
    color: "#ADADAD",
    cursor: "pointer",
    transition: "all 0.3s ease-in-out",
    margin: "0 16px 0 0px",
    fontSize: "35px",
  },
  _save: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#6F9CEB",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _close: {
    "&:hover": {
      transform: "scale(1.3)",
      color: "#525252",
    },
    "&:hover + span": {
      display: "block",
    },
  },
  _subheading: {
    font: "normal normal 500 22px/32px Roboto",
    color: " #121212",
    marginTop: "44px",
    marginBottom: "40px",
  },
  _dflex: {
    display: "flex",
    alignItems: "center",
  },
});

const PermissionForm = ({ initialValues, onSubmit, action, modules = [] }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: true,
    validationSchema: SupplierGroupSchema,
    initialValues: {
      name: "",
      ...initialValues,
    },
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        let snakeCaseName = "";
        if (values.name) {
          snakeCaseName = values.name
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_");
        }

        const payload = {
          name: snakeCaseName,
          module: values.module,
          default: values.default,
        };

        await onSubmit(payload);
      } catch (err) {
        console.error("Submit error:", err);
      } finally {
        setSubmitting(false);
      }
    }

  });

  const {
    values,
    handleChange,
    setFieldValue,
    errors,
    handleSubmit,
    submitCount,
    isValid,
    isSubmitting,
    setFieldTouched,
  } = formik;
  let { handleBlur } = formik;

  if (!submitCount) {
    handleBlur = null;
  }
  const closeCustomerHandler = () => {
    action === "ADD"
      ? history.push(PATHS.admin.permissions)
      : history.push(PATHS.admin.permissionsDetail.replace(":id", id));
  };
  const moduleOptions = modules.map((mod) => ({ name: mod }));
  const cancelButtonRef = useRef(null);
  const saveButtonRef = useRef(null);
  return (
    <Box height="100%">
      <Box display="flex" mb={4}>
        <Box flex={2}>
          <Typography className={classes._heading} variant="h4">
            {action === "ADD" ? t("New Permission") : t("Edit Permission")}
          </Typography>
        </Box>
        <Box flex={2} textAlign="right" position="relative">
          <Box position="absolute" right={0}>
            <FormCancelSaveButton
              disabled={!isValid || isSubmitting}
              onCancel={closeCustomerHandler}
              onSave={handleSubmit}
              ref={{ cancelRef: cancelButtonRef, saveRef: saveButtonRef }}
            />
          </Box>
        </Box>
      </Box>
      <Typography className={classes._subheading} variant="h5">
        {t("Basic Data")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Input
            label={t("Name")}
            name="name"
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
            errors={errors}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Autocomplete
            onBlur={handleBlur}
            name="module"
            label={t("Module")}
            errors={errors}
            value={values.module}
            settings={{
              disableClearable: true,
              valueProp: "name",
              labelProp: "name",
              disabled: action === "EDIT"
            }}
            onChange={(selected) => {
              setFieldValue("module", selected?.name || "");
            }}
            options={moduleOptions}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <Box display="flex" flexDirection="row" alignItems="center" mt={1}>
            <Checkbox
              name="default"
              checked={Boolean(values.default)}
              onChange={(e) => {
                setFieldValue('default', e.target.checked);
              }}
              onBlur={handleBlur}
              style={{ color: "#6F9CEB" }}

            />
            <Typography component="span" className="font-size-16">
              {t("Default Value")}
            </Typography>
          </Box>
        </Grid>
      </Grid>

    </Box>
  );
};
export default PermissionForm;
